import { makeAutoObservable } from "mobx";
import { ZEntityError } from "src/business/error/ZEntityError";
import { debounce, DebounceCounter } from "src/common/debounce";
import { cvtError, onError } from "src/common/onError";

import { msgQueueStore } from "../MsgQueue";
import { activateByPath } from "./formNav/activateByPath";
import { findAllFields } from "./formNav/findAllFields";
import { recalcNode } from "./formNav/recalcNodes";
import { MfErrors } from "./MfError";
import { mfPath2str } from "./MFormTypes";
import { ExprBoolCtx } from "./node/createExprBool";
import { createMfNode } from "./node/createMfNode";
import { MfNode } from "./node/MfNode";
import { MfNodeAttr } from "./node/MfNodeAttr";
import { execValidators } from "./validators/execValidators";
import { ZMfNode } from "./ZMfNode";

export type MFormParams = {
  onChange?(changedFields: Set<string>): void;
};

export class MFormStore {
  constructor(public readonly params: MFormParams = {}) {
    makeAutoObservable(this);
  }

  meta: ZMfNode = { type: "grid", nodes: [] };

  root: MfNode = createMfNode(this.meta, []);

  /**
   * Инициализация для простых случаев
   */
  initStd(meta: ZMfNode, data: unknown, externalErrors?: ZEntityError[]) {
    this.setMeta(meta);
    this.setInitialValues(data);
    this.setExternalErrors(externalErrors ?? []);
  }

  /**
   *
   * @param node
   * @param noInit Не указывается для простых случаев. NoInit, если метаданные меняются в процессе редактирования формы.
   * (Да! Бывает и такое. Например, карточка записи класса. Там можно убрать/добавить спецификацию, что приводит к изменению списка блоков)
   */
  setMeta(node: ZMfNode, mode?: "NoInit") {
    const prevValue = this.root.value;
    this.meta = node;
    this.root = createMfNode(node, []);

    if (mode === "NoInit") {
      this.root.setValue(prevValue);
      return;
    }

    this.onChange();
    this.root.setAsUnchanged();
  }

  get fieldsValue(): unknown {
    return this.root.value;
  }

  setFieldsValue(value: unknown) {
    this.root.setValue(value);
    this.onChange();
  }

  setInitialValues(value: unknown) {
    this.setFieldsValue(value);
    this.setAsUnchanged();
    this.setTouched(new Set());
  }

  saving: boolean = false;

  setSaving(value: boolean) {
    this.saving = value;
  }

  async onFinish<Res>(
    save: (data: unknown) => Promise<Res>,
  ): Promise<Error | Res> {
    try {
      const firstError = Object.values(this.errors)[0];
      this.setTouched("all");
      if (firstError) {
        activateByPath(this.root, firstError.node.dataPath);
        return firstError.error;
      }

      this.setSaving(true);
      const result = await save(this.fieldsValue);
      this.setAsUnchanged();
      return result;
    } catch (srcError) {
      const e = cvtError(srcError);
      onError(e);
      return e;
    } finally {
      this.setSaving(false);
    }
  }

  /**
   * Все узлы получают initialData из текущих данных.
   * В результате isChanged будет false.
   * Автоматически вызывается из setInitialValues
   * И из onFinish в случае успешного сохранения данных.
   *
   * В явном виде имеет смысл вызывать в случае принудительного покидания страницы (например, если текущая карточка удаляется)
   * чтобы не выдавалось предупреждение о несохранённых данных.
   */
  setAsUnchanged() {
    this.root.setAsUnchanged();
  }

  /**
   * Признак наличия изменений на форме.
   * Обычно используется для выдачи предупреждения при попытке покинуть страницу.
   */
  get isChanged() {
    return this.root.isChanged;
  }

  // Множество полей, изменённых в
  lastChanged = new Set<string>();

  onValueChange(node: MfNode) {
    const { dataPath } = node;
    if (dataPath) {
      const strPath = mfPath2str(dataPath);
      this.lastChanged.add(strPath);
    }
  }

  /**
   * Стандартное сохранение данных.
   * Предполагается, что если save ничего не возвращает, то данные остаются без изменений.
   * А если возвращается что-то, кроме ошибки, то эти данные перезаписываются в поля.
   *
   * Если стандартное поведение не подходит, то нужно напрямую вызывать и обрабатывать onFinish.
   */
  finish(save: (data: unknown) => Promise<unknown | void>) {
    this.onFinish(save)
      .then((res) => {
        if (!(res instanceof Error)) {
          msgQueueStore.add({
            type: "success",
            message: "Выполнено сохранение данных",
          });
          if (res) {
            this.root.setValue(res);
          }
        }
      })
      .catch(onError);
  }

  errors: MfErrors = {};

  setErrors(dict: MfErrors) {
    this.errors = dict;
  }

  addError(error: Error, node: MfNode) {
    const path = mfPath2str(node.dataPath);
    this.errors[path] = { error, node };
  }

  externalErrors: ZEntityError[] = [];

  setExternalErrors(list: ZEntityError[]) {
    this.externalErrors = list;
  }

  get externalErrorsByPath(): Record<string, ZEntityError[]> {
    const result: Record<string, ZEntityError[]> = {};
    this.externalErrors.forEach((err) => {
      if (err.attributePath) {
        const prev = result[err.attributePath] ?? [];
        result[err.attributePath] = [...prev, err];
      }
    });
    return result;
  }

  // Механизм посещения полей
  touched: Set<string> | "all" = new Set();

  setTouched(fields: Set<string> | "all") {
    this.touched = fields;
  }

  isTouched(dataPath: string): boolean {
    const { touched } = this;
    return touched === "all" || touched.has(dataPath);
  }

  touch(dataPath: string) {
    const { touched } = this;
    if (touched !== "all") {
      touched.add(dataPath);
    }
  }

  onChange(secondary: boolean = false) {
    debounce(this.changeCounter, 50, () => {
      this.startValidating();
      const { root } = this;
      let counter = 0;
      const ctx: ExprBoolCtx = {
        root,
        onVisibilityChange() {
          counter++;
        },
      };
      recalcNode(root, ctx);
      this.params.onChange?.(this.lastChanged);
      this.lastChanged.clear();
      if (counter && !secondary) {
        // В результате изменения видимости элементов, могут появиться новые поля с ошибками (или исчезнуть)
        // Но бесконечно входить в такой цикл не нужно.
        this.onChange(true);
      }
    });
  }

  validationTask: Promise<void> | null = null;

  setValidationTask(task: Promise<void> | null) {
    this.validationTask = task;
  }

  needValidation = false;

  setNeedValidation(value: boolean) {
    this.needValidation = value;
  }

  startValidating() {
    if (!this.validationTask) {
      this.setNeedValidation(false);
      const task = this.createValidationTask();
      this.setValidationTask(task);
      task
        .then(() => {
          this.setValidationTask(null);
          if (this.needValidation) {
            this.startValidating();
          }
        })
        .catch(onError); // <- если ошибка дошла сюда, то это баг, т.к. ошибки валидации должны отлавливаться
    } else {
      this.setNeedValidation(true);
    }
  }

  changeCounter: DebounceCounter = {};

  protected async createValidationTask() {
    this.setErrors({});
    await Promise.all(
      this.allFields.map((field) =>
        execValidators(field).catch((e) => {
          this.addError(e, field);
        }),
      ),
    );
  }

  get allFields(): MfNodeAttr[] {
    const fields: MfNodeAttr[] = [];
    if (this.changeCounter) {
      findAllFields(this.root, fields);
    }
    return fields;
  }
}
